import { Editor } from "@tiptap/core"
import { compareAsc, startOfDay } from "date-fns"
import { EditorView } from "prosemirror-view"

import { isLocalHost, isStaging } from "@/app.config"
import { isImage } from "@/helpers/isImg"
import { isVideo } from "@/helpers/isVideo"
import { tempAttachmentId } from "@/helpers/tempAttachmentId"
import { FilterBacklog, ItemStatus, TypeConfig, UploadAttachmentFn, User } from "@/models"
import { ExtendValueForChart } from "@/models/Chart/Chart"
import { ReferenceType } from "@/plugins/extensions/ReferenceType"
import { $t } from "@/plugins/i18n"
import { Actions, Getters, Modules } from "@/store"

export const timeUnitList = ["second", "minute", "hour", "day"]

export type TimeUnit = (typeof timeUnitList)[number]

type ObserverOptions = ConstructorParameters<typeof IntersectionObserver>[1]

export const observerOptions: ObserverOptions = {
  root: null,
  rootMargin: "0px 0px 240px 0px",
  threshold: 0.5
}

const getMap = (modules: Modules, actions: typeof Actions | typeof Getters, isGetter?: boolean) => {
  const map = {}

  for (const key in actions) {
    map[key] = isGetter ? `${modules}/get/${key}` : `${modules}/${key}`
  }
  return map
}

export const addClassToBody = (className: string) => document.body.classList.add(className)

export const removeClassFromBody = (className: string) => document.body.classList.remove(className)

export const replaceTextDecorationTags = html =>
  html.replace(/style="[^"]*"/gi, "").replace(/<\/?(b|i|u|s|sup|sub|font|strong|em)\s*>/gi, "")

export const compareOnlyDates = (startDate: Date, enDate: Date) =>
  compareAsc(startOfDay(startDate), startOfDay(enDate))

export const replaceHtmlTags = html =>
  html
    ? html
        .replace(/<\/?[a-zA-Z]+>/gi, "")
        .replace(/&nbsp;/g, " ")
        .trim()
    : ""

export const replaceHtmlTagsV2 = html =>
  html
    ? html
        .replace(/<\/?[a-zA-Z]+[^>]*>/gi, "")
        .replace(/&nbsp;/g, " ")
        .trim()
    : ""

export const replaceHtmlTagsForEditor = html =>
  html
    .replace(/<(\/?)(p|br|span)([^>]*)>/gi, tag => (tag.includes("style=") ? tag : ""))
    .replace(/&nbsp;/g, " ")
    .trim()

export const replaceHtmlTagsForEditorWithoutTrim = html => {
  return html
    .replace(/<(\/?)(p|br|span)([^>]*)>/gi, (slash, tagName, rest) => {
      const newRest = rest.replace(/\s*style="[^"]*"/gi, "")
      return `<${slash}${tagName}${newRest}>`
    })
    .replace(/&nbsp;/g, " ")
}
export const replaceDashToSpace = text => text.replace(/-+/g, " ")

export const toLowerCaseWithTrim = text => text.toLowerCase().trim()

export const checkValidDate = dateString => {
  const dateWithoutTime = dateString.toString().split("T")[0]
  const regex = /^\d{4}-\d{2}-\d{2}$/
  return regex.test(dateWithoutTime)
}

export const stripHtml = html => {
  const tmp = document.createElement("div")
  tmp.innerHTML = html
  return tmp.textContent || tmp.innerText || ""
}

export const setBacklogQuery = mainId => {
  const query = (mainId ? mainId : "BacklogProduct") as string
  let main = $t("backlog.query.BacklogProduct")
  if (query.includes("BacklogSprint")) {
    const sequenceNumber = query.split("#")[1]
    main = `${$t("backlog.query.BacklogSprint")}#${sequenceNumber}`
  } else {
    main = $t(`backlog.query['${query}']`)
  }

  return main
}

export const hideElementByAccount = (currentUser: User, customAccountNames?: string[]) => {
  if (currentUser.superAdmin) return false
  if (!isStaging() && !isLocalHost()) {
    return !currentUser.accounts.find(
      account =>
        account.urlName === "project-simple" || customAccountNames?.includes(account.urlName)
    )
  }
  return false
}

export const parseBacklogQuery = queryMain => {
  const query = (
    queryMain && queryMain.length && queryMain !== "currentSprint" ? queryMain : "product"
  ) as string
  let main = $t("backlog.main.product")

  if (query?.includes("sprint")) {
    const sequenceNumber = query?.split("#")[1]
    main = `${$t("backlog.main.sprint")}#${sequenceNumber}`
  } else {
    main = $t(`backlog.main['${query}']`)
  }

  return main
}

export const getActionsMap = (modules: Modules, actions: typeof Actions) => getMap(modules, actions)

export const getGettersMap = (modules: Modules, actions: typeof Getters) =>
  getMap(modules, actions, true)

export const stepBackInBrowserHistory = route =>
  (!route.query?.login && !!window.history.state.back) || route.query?.i

export interface InsertFilePreviewPayload {
  file: {
    id: string
    name: string
  }
  position?: number
  view: EditorView
}

export type InsertFilePreview = (payload: InsertFilePreviewPayload) => void

export const uploadAttachments = async ({
  upload,
  files,
  view,
  config,
  position,
  type,
  insertFilePreview
}: {
  upload: UploadAttachmentFn
  files
  view
  config: TypeConfig | null
  type?: string
  position?: number
  insertFilePreview?: InsertFilePreview
}) => {
  const { schema } = view.state
  const insertPosition = position || view.state.selection.$to.pos + 1

  if (!type) {
    const tempNode = schema.nodes.attachment.create({
      id: tempAttachmentId(files[0].name),
      title: files[0].name,
      isSkeletor: true
    })
    view.dispatch(view.state.tr.insert(insertPosition, tempNode))
  }

  let filesMetadata
  try {
    filesMetadata = await upload({
      attachments: files as Blob[],
      ...(config
        ? {
            resourceType: config.type,
            targetId: config.targetId,
            excludeDuplicate: true
          }
        : {})
    })
  } catch (error) {
    console.error("Error uploading files:", error)
  }

  let deleteFrom = null
  let deleteTo = null

  view.state.doc.descendants((node, pos) => {
    if (node.attrs.isSkeletor) {
      deleteFrom = pos
      deleteTo = pos + node.nodeSize
    }
  })

  if (deleteFrom !== null && deleteTo !== null) {
    view.dispatch(view.state.tr.delete(deleteFrom, deleteTo))
  }

  if (!filesMetadata) return {}

  const previewFile = insertFilePreview ?? insertAttachmentPreview

  filesMetadata.forEach(file => {
    previewFile({ file, position: insertPosition, view })
  })
  return {}
}

export const insertAttachmentPreview = ({ file, position, view }: InsertFilePreviewPayload) => {
  const attachmentType = isImage(file.name) ? "img" : isVideo(file.name) ? "video" : "file"
  const node = view.state.schema.nodes.attachment.create({
    id: file.id,
    title: file.name,
    info: JSON.stringify(file),
    linkText: "",
    type: attachmentType
  })
  const transaction = view.state.tr.insert(position || 0, node)
  view.dispatch(transaction)
}

export const filePreviewForEditor = ({
  file,
  editor,
  position,
  view
}: InsertFilePreviewPayload & { editor: Editor }) => {
  const node = editor.schema.nodes.referenceHashFile.create({
    id: file.id,
    info: JSON.stringify(file),
    loading: false,
    configType: ReferenceType.FILE,
    linkText: "",
    title: file.name
  })
  const transaction = view.state.tr.insert(position || 0, node)
  view.dispatch(transaction)
}

export const inputOnlyNumber = event => {
  // Allow input if it's not a single character (e.g., Shift, Ctrl, etc.)
  if (event.key.length > 1) return

  // If the entered character is not a digit, block the input
  if (!/\d/.test(event.key)) event.preventDefault()
}

export const sortTask = (a, b) => Number(a.order) - Number(b.order)

export const wait = milliseconds => new Promise(resolve => setTimeout(resolve, milliseconds))

export const getImageSize = async (url: string): Promise<{ width; height }> =>
  new Promise(resolve => {
    const img = document.createElement("img")
    img.src = url
    img.onload = () => {
      resolve({ width: img.width, height: img.height })
    }
  })

export const parsingProjectIdAndItemNumber = (itemParams: string) => {
  const parts = itemParams.split("-")
  const itemId = parts.pop() as string
  const projectId = parts.join("-")
  return {
    itemId,
    projectId
  }
}

export const capitalizeOnlyFirstLetter = string =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()

export const parsingServerResponseContent = ({
  originalContent,
  serverResponseContent,
  numberOfPage,
  originalNumberOfPage
}: {
  originalContent: any[]
  serverResponseContent: any[]
  numberOfPage: number
  originalNumberOfPage?: number
}) => {
  if (numberOfPage !== 0 && numberOfPage === originalNumberOfPage) {
    const copyOriginalContent = [...originalContent]
    serverResponseContent.forEach(item => {
      if (originalContent.some(originalItem => originalItem.id === item.id)) return
      copyOriginalContent.push(item)
    })
    return copyOriginalContent
  }

  if (numberOfPage === 0) {
    return serverResponseContent
  }

  const existingIds = new Set(originalContent.map(item => item?.id).filter(Boolean))
  const nextPageUniqueItems = serverResponseContent.filter(item => !existingIds.has(item?.id))

  return [...originalContent, ...nextPageUniqueItems]
}

export const splitAndCapitalize = (string: string, delimiter = "_") => {
  if (!string) return ""
  return string
    .toString()
    .split(delimiter)
    .map(word => capitalizeOnlyFirstLetter(word))
    .join(" ")
}

export const getInitials = (name: string) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [firstPart, lastPart, ..._rest] = name.split(" ")

  let numberOfSymbols = 1
  if (!lastPart) {
    numberOfSymbols = 2
  }

  const initials = firstPart.substring(0, numberOfSymbols) + (lastPart?.substring(0, 1) ?? "")
  return initials.toUpperCase()
}

export const searchItemByText = value => {
  const trimValue = value.trim()
  const id = /^[0-9]+$/.test(trimValue) ? "sequenceNumberPrefix" : "text"
  return {
    id,
    value: trimValue
  }
}

export const intervalCallback = <T>(config: {
  data: T[]
  interval: number
  callback: (element: T) => void
  onIntervalEndCallback?: () => void
}) => {
  let iterationIndex = 0

  const intervalId = setInterval(() => {
    const dataElement = config.data.at(iterationIndex)
    if (!dataElement) {
      // iteration is out of data array range
      clearInterval(intervalId)
      config.onIntervalEndCallback?.()
      return
    }

    config.callback(dataElement)
    iterationIndex++
  }, config.interval)
}

export const getValueByVariableName = ({
  variableName,
  isDark
}: {
  variableName: string
  isDark?: boolean
}) => {
  const root = isDark
    ? (document.querySelector(":root.dark") as HTMLElement)
    : (document.querySelector(":root") as HTMLElement)

  if (!root) {
    return ""
  }

  return getComputedStyle(root)?.getPropertyValue(variableName) as any
}

export const excludedStatusFromBacklog = ({
  backlogFilter,
  itemStatus
}: {
  backlogFilter: FilterBacklog
  itemStatus: ItemStatus
}) => {
  if (backlogFilter.storyStatuses?.length && !backlogFilter.storyStatuses.includes(itemStatus))
    return true
  return (
    !backlogFilter.storyStatuses?.includes(itemStatus) &&
    backlogFilter.excludeStoryStatuses?.includes(itemStatus)
  )
}

export const emailValidate = (email: string): boolean => {
  const regex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
  return regex.test(email)
}

export const parsingUrlName = value => {
  const REGEX = /[^a-zA-Z0-9]/g

  const editedValue = value.replace(REGEX, "-").replace(/-+/g, "-").toLowerCase()
  return editedValue
}

export const extendValueForChart = (params: ExtendValueForChart) => {
  const { datasets, step } = params
  if (!datasets?.length) return 0
  const percent = params.percent / 100
  const data: number[] = []
  datasets.forEach(dataset => {
    dataset.data.forEach(value => {
      data.push(value)
    })
  })
  // Get the max value from the data
  const maxDataValue = Math.max(...data)
  // Add percent to the max value to make the chart look better
  const maxValue = Math.ceil((maxDataValue + maxDataValue * percent) / step) * step
  return maxValue === maxDataValue ? maxValue + step : maxValue
}

export const parsePrefix = (name: string) => {
  // Remove special characters except for spaces
  const cleanedName = name.replace(/[^A-Za-z0-9\s]/g, "")
  // Split the cleaned name into words
  const words = cleanedName.split(/\s+/)
  // Take the first letter of the first two words
  let prefix = ""
  if (words.length === 1) {
    prefix = words[0].slice(0, 2).toUpperCase()
  } else {
    prefix = (words[0][0] + words[1][0]).toUpperCase()
  }

  return prefix
}

export const toFixedNumber = (value: number, precision?: number) => {
  const number = value ? Number(value) : 0
  return number ? Number(number.toFixed(precision ?? 2)) : 0
}

export const convertMsToTime = (ms: number, type: TimeUnit) => {
  let time = ms / 1000
  switch (type) {
    case "second":
      time = ms / 1000
      break
    case "minute":
      time = ms / 1000 / 60
      break
    case "hour":
      time = ms / 1000 / 60 / 60
      break
    case "day":
      time = ms / 1000 / 60 / 60 / 24
      break
    case "week":
      time = ms / 1000 / 60 / 60 / 24 / 7
      break
    case "month":
      time = ms / 1000 / 60 / 60 / 24 / 30
      break
    case "year":
      time = ms / 1000 / 60 / 60 / 24 / 365
      break
    default:
      break
  }
  return toFixedNumber(time)
}

export const checkTimeUnit = (ms: number): TimeUnit => {
  const time = ms / 1000
  if (time < 60) return "second"
  if (time < 3600) return "minute"
  if (time < 86400) return "hour"
  return "day"
}

export const normalizeParam = (value: string | string[] | null | undefined): string | null => {
  if (Array.isArray(value)) {
    value = value[0]
  }
  return value && value !== "undefined" ? value : null
}

export const isTempAccountName = (accountName: string): boolean => {
  if (typeof accountName !== "string") return false
  return /^temp_[a-z\u0400-\u04FF0-9_]+_\d{13}$/.test(accountName.trim())
}

export const fixColWidthForEditorTable = node => {
  // Base case: If the node is not a traversable object (e.g., null, string, number), stop the recursion.
  if (!node || typeof node !== "object") {
    return node
  }

  // If the current node is an array, iterate over it and process each element recursively.
  if (Array.isArray(node)) {
    node.forEach(fixColWidthForEditorTable)
    return node // Return after processing the array elements.
  }

  // Core logic: Check if the node has an 'attrs' property, and if 'colwidth' within it is an array.
  if (node.attrs && Array.isArray(node.attrs.colwidth)) {
    // If it is, join the array elements into a single comma-separated string.
    // e.g., [100, 200, 150] becomes "100,200,150".
    node.attrs.colwidth = node.attrs.colwidth.join(",")
  }

  // For any other object, recursively call the function for each of its property values
  // to ensure we traverse the entire nested structure.
  Object.keys(node).forEach(key => {
    fixColWidthForEditorTable(node[key])
  })
  return node
}
